import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { getChildValueAsString, IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { EditorLabel } from './EditorLabel';
import { StringComboBox, IStringComboOption } from 'components/inputs/StringComboBox';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { useEditorId } from './hooks/useEditorId';
// import './ChartPartPickerEditor.less'

interface IChartPartPickerEditorProps extends IEditorProps {
  className?: string;
}

const getClassName = (className?: string) => classNames('dpc-chart-part-picker', className);

/**
 * Chart Part Picker. A combobox which gets its options from chart-part-specific
 * EditorConfigValues, where the name is a Chart name and the value is a GUID
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ChartPartPickerEditor({ child, className, path, onChange }: IChartPartPickerEditorProps) {
  const [id] = useEditorId(path);
  const validation = getEditorValidationSummary(child);
  const chartId = getChildValueAsString(child);
  const [currentChartId, setCurrentChartId] = useState<string>(chartId);
  const options = useMemo(() => getOptions(child), [child]);

  const selectedOption = useMemo(() => {
    return options.find((op: IStringComboOption<String>) => op.value === chartId);
  }, [options, chartId]);

  useEffect(() => {
    const value = (selectedOption && selectedOption.value) || '';
    setCurrentChartId(value);
  }, [selectedOption, chartId]);

  const onValueChange = (value: string | null) => {
    onChange(value, path);
    setCurrentChartId(value || '');
  };

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} className={getClassName(className)}>
      <StringComboBox
        id={id}
        options={options}
        onValueChange={onValueChange}
        currentValue={currentChartId}
        validation={validation}
        allowFreeText={false}
        hideClear={true}
      />
    </EditorLabel>
  );
}

function getOptions(child: IPropertyChild) {
  const availableChartParts: DataPair[] = getEditorConfigValue(child, 'AvailableChartParts') || [];
  return availableChartParts.map((val: DataPair) => ({ text: val.Name, value: val.OutputValue }));
}
